import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogClose,
} from '@angular/material/dialog';
import { endOfDay, format, parseISO, subDays } from 'date-fns';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from '../../services/workflow/workflow.service';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeDatepickerComponent } from '../../payee-form-fields/payee-datepicker/payee-datepicker.component';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { TranslateModule } from '@ngx-translate/core';

type DialogData = {
  transition_label?: string;
  mother_name: string | null;
  birth_place: string | null;
  birth_date: string | null;
};

@Component({
  selector: 'app-debtor-ind-data-dialog',
  templateUrl: './debtor-ind-data-dialog.component.html',
  styleUrls: ['./debtor-ind-data-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeDatepickerComponent,
    PayeeButtonComponent,
    PayeeIconComponent,
    MatDialogClose,
    TranslateModule,
  ],
})
export class DebtorIndDataDialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  private data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);
  private dialogRef =
    inject<MatDialogRef<DebtorIndDataDialogComponent>>(MatDialogRef);
  private workflowService = inject(WorkflowService);

  loading = false;
  dialogDataLoading = true;

  readonly form = this.fb.group({
    birth_date: this.fb.control<Date | null>(null, Validators.required),
    birth_place: this.fb.control<string | null>(null, [
      Validators.required,
      Validators.maxLength(255),
    ]),
    mother_name: this.fb.control<string | null>(null, [
      Validators.required,
      Validators.maxLength(255),
    ]),
  });

  readonly maxBirthDate = endOfDay(subDays(new Date(), 1));

  get birth_date() {
    return this.form.controls.birth_date;
  }
  get birth_place() {
    return this.form.controls.birth_place;
  }
  get mother_name() {
    return this.form.controls.mother_name;
  }

  async ngOnInit(): Promise<void> {
    try {
      const dialogData = await this.workflowService.getDialogData<DialogData>({
        caseId: this.data.caseId,
        transitionId: this.data.transitionId,
      });

      this.form.patchValue({
        mother_name: dialogData.mother_name ?? null,
        birth_place: dialogData.birth_place ?? null,
        birth_date: dialogData.birth_date
          ? parseISO(dialogData.birth_date)
          : null,
      });
    } catch (error) {
      console.error('Error loading debtor ind dialog data', error);
    } finally {
      this.dialogDataLoading = false;
    }
  }

  async submit() {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((c) => c.markAsDirty());
      return;
    }

    const { birth_date, birth_place, mother_name } = this.form.getRawValue();
    if (!birth_date) {
      return;
    }

    const closeValue: RunTransition = {
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
      params: {
        ...(this.data.extraParams ?? {}),
        birth_date: format(birth_date, 'yyyy-MM-dd'),
        birth_place,
        mother_name,
      },
    };

    this.loading = true;
    try {
      await this.workflowService.runTransition(closeValue);
      this.dialogRef.close(closeValue);
    } catch (error) {
      this.workflowService.showTransitionError(error);
    } finally {
      this.loading = false;
    }
  }
}
